import * as React from "react";
import { useEffect, useState } from "react";
import { getPalette } from "./RuleResultItem";
import type { RunStatusType } from "./RulesResultPanel";

export interface RunStatusIndicatorProps {
  status: RunStatusType;
}

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/** SSR/jsdom-safe synchronous read of the current prefers-reduced-motion state. */
function readPrefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

/**
 * `MediaQueryList`'s pre-standard `addListener`/`removeListener` members, typed locally so the IE11
 * fallback below never references the DOM lib's own deprecated signatures directly (S1874).
 */
type LegacyMediaQueryList = {
  addListener?: (callback: (event: MediaQueryListEvent) => void) => void;
  removeListener?: (callback: (event: MediaQueryListEvent) => void) => void;
};

/**
 * Tracks the `prefers-reduced-motion` OS/browser setting so the RUNNING pulse can be suppressed for
 * users who asked to reduce motion. `colors.css` also carries a `@media` fallback at the CSS level
 * — this hook is belt-and-suspenders, and what unit tests exercise (jsdom doesn't apply real
 * stylesheets/@media rules, and doesn't implement matchMedia at all — see jest.setup.js).
 *
 * Exported so `RulesResultPanel.tsx`'s status-label shimmer can reuse this hook instead of
 * duplicating it.
 */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(readPrefersReducedMotion);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mql = window.matchMedia(REDUCED_MOTION_QUERY);
    setReduced(mql.matches);

    const handleChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", handleChange);
      return () => mql.removeEventListener("change", handleChange);
    }
    // Legacy fallback (e.g. IE11): addListener/removeListener are deprecated in favor of
    // addEventListener, but IE11 never implemented the latter on MediaQueryList.
    const legacy = mql as unknown as LegacyMediaQueryList;
    if (legacy.addListener) {
      legacy.addListener(handleChange);
      return () => legacy.removeListener?.(handleChange);
    }
    return undefined;
  }, []);

  return reduced;
}

const INDICATOR_SIZE = 18;

/** Shared geometry for every indicator variant — keeps row alignment identical across statuses. */
const baseIndicatorStyle: React.CSSProperties = {
  display: "inline-flex",
  width: INDICATOR_SIZE,
  height: INDICATOR_SIZE,
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  flex: "0 0 auto",
};

/**
 * Per-status left indicator for a rule result row. The shape is derived here from `status` —
 * `getPalette` only supplies color tokens + statusLabel:
 *  - RUNNING: a blue ring with a centered dot that pulses (reduced-motion => static dot).
 *  - QUEUED: a gray circle with the left half filled. Always static — only the status label
 *    shimmers for QUEUED, never this icon.
 *  - CANCELED: a gray filled circle with a thick white horizontal bar (deny symbol) — not the
 *    white ✕ glyph, to distinguish it from FAILED.
 *  - VERIFIED / FAILED / ERROR (and any unrecognised status): filled circle + white glyph from
 *    `palette.badgeGlyph`.
 *
 * Always `aria-hidden`: the text status label conveys the meaning for screen readers. Shapes/
 * glyphs (not color alone) make every status pair distinguishable.
 */
export const RunStatusIndicator: React.FC<RunStatusIndicatorProps> = ({ status }) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const palette = getPalette(status);
  const testId = `run-status-indicator-${status.toLowerCase()}`;

  if (status === "RUNNING") {
    return (
      <span
        aria-hidden
        data-testid={testId}
        style={{
          ...baseIndicatorStyle,
          boxSizing: "border-box",
          background: "transparent",
          border: `2px solid ${palette.accent}`,
        }}
      >
        <span
          data-testid={`${testId}-dot`}
          className={prefersReducedMotion ? undefined : "run-status-pulse-dot"}
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: palette.accent,
          }}
        />
      </span>
    );
  }

  if (status === "QUEUED") {
    return (
      <span
        aria-hidden
        data-testid={testId}
        style={{
          ...baseIndicatorStyle,
          boxSizing: "border-box",
          border: `1px solid ${palette.accent}`,
          backgroundImage: `linear-gradient(to right, ${palette.badgeBg} 0 50%, transparent 50% 100%)`,
        }}
      />
    );
  }

  if (status === "CANCELED") {
    return (
      <span
        aria-hidden
        data-testid={testId}
        style={{
          ...baseIndicatorStyle,
          background: palette.badgeBg,
        }}
      >
        <span
          data-testid={`${testId}-bar`}
          style={{
            width: "70%",
            height: 3,
            borderRadius: 2,
            background: palette.badgeFg,
          }}
        />
      </span>
    );
  }

  // VERIFIED / FAILED / ERROR / any unrecognised status: unchanged badge + glyph.
  return (
    <span
      aria-hidden
      data-testid={testId}
      style={{
        ...baseIndicatorStyle,
        background: palette.badgeBg,
        color: palette.badgeFg,
        fontSize: 11,
        fontWeight: 700,
      }}
    >
      {palette.badgeGlyph}
    </span>
  );
};
